import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { FilterList, Menu as MenuIcon, Refresh } from '@mui/icons-material';
import TvIcon from '@mui/icons-material/Tv';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useNavigate } from 'react-router-dom';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { screenDialogBodySx } from '../screens/dialogSx';
import { screensApi } from '../screens/Screens.api';
import { Screen, formatAgo } from '../screens/Screens.types';
import { ON_DEMAND_PATH } from './OnDemand.types';

const REFRESH_MS = 10000;
const VIEWMODE_STORAGE_KEY = 'on-demand-player-view-mode';

type ScreenViewMode = 'tile' | 'table';
type ScreenSortField = 'name' | 'lastSeenAt' | 'status' | 'version';
type ScreenSortOrder = 'asc' | 'desc';

const compareScreens = (a: Screen, b: Screen, field: ScreenSortField, order: ScreenSortOrder) => {
    const dir = order === 'asc' ? 1 : -1;
    const value = (screen: Screen): string | number => {
        switch (field) {
            case 'status':
                return screen.online ? 1 : 0;
            case 'version':
                return screen.deviceStatus?.clientVersion || '';
            case 'lastSeenAt':
                return screen.lastSeenAt || '';
            default:
                return screen.name.toLowerCase();
        }
    };
    const left = value(a);
    const right = value(b);
    if (left < right) return -1 * dir;
    if (left > right) return 1 * dir;
    return a.name.localeCompare(b.name);
};

/** Beam on Screen → On Demand Player screen list (`/#/beamonscreen/ods`). */
export const OnDemandScreensPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const heroGradient =
        theme.palette.dashboard?.counterHeroGradient ??
        `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`;
    const [screens, setScreens] = useState<Screen[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState<ScreenSortField>('name');
    const [sortOrder, setSortOrder] = useState<ScreenSortOrder>('asc');
    const [now, setNow] = useState(() => Date.now());
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
    const [viewMode, setViewMode] = useState<ScreenViewMode>(() => {
        try {
            return localStorage.getItem(VIEWMODE_STORAGE_KEY) === 'table' ? 'table' : 'tile';
        } catch {
            return 'tile';
        }
    });

    const load = useCallback(async () => {
        try {
            const data = await screensApi.list();
            setScreens(data.screens);
            setNow(Date.now());
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load screens');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
        const timer = window.setInterval(() => void load(), REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [load]);

    const setView = (next: ScreenViewMode) => {
        setViewMode(next);
        try {
            localStorage.setItem(VIEWMODE_STORAGE_KEY, next);
        } catch {
            /* ignore */
        }
    };

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = q
            ? screens.filter((screen) =>
                  [screen.name, screen.location, screen.deviceStatus?.hostname, screen.deviceStatus?.ip, screen.deviceStatus?.resolution].some((value) =>
                      (value || '').toLowerCase().includes(q),
                  ),
              )
            : screens;
        return [...filtered].sort((a, b) => compareScreens(a, b, sortField, sortOrder));
    }, [screens, search, sortField, sortOrder]);

    const openScreen = (screenId: string) => navigate(`${ON_DEMAND_PATH}/${screenId}`);

    const filterControls = (fullWidth: boolean) => (
        <>
            <FormControl sx={fullWidth ? undefined : { minWidth: 160 }} size="small" fullWidth={fullWidth}>
                <InputLabel>Sort By</InputLabel>
                <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value as ScreenSortField)}>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="lastSeenAt">Last Seen</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                    <MenuItem value="version">Version</MenuItem>
                </Select>
            </FormControl>
            <FormControl sx={fullWidth ? undefined : { minWidth: 100 }} size="small" fullWidth={fullWidth}>
                <InputLabel>Order</InputLabel>
                <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as ScreenSortOrder)}>
                    <MenuItem value="asc">ASC</MenuItem>
                    <MenuItem value="desc">DSC</MenuItem>
                </Select>
            </FormControl>
        </>
    );

    const viewToggle = (
        <ToggleButtonGroup size="small" exclusive value={viewMode} onChange={(_e, next: ScreenViewMode | null) => next && setView(next)} aria-label="Screen layout">
            <ToggleButton value="tile" aria-label="Tile view">
                <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
            </ToggleButton>
        </ToggleButtonGroup>
    );

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: ON_DEMAND_PATH, itemLabel: 'On Demand Player' },
                ]}
            />
            {loading && screens.length === 0 && (
                <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }} open>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center',
                    mb: 3,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton color="secondary" onClick={() => setMobileFiltersOpen(true)} size="large" aria-label="Search and filters" sx={{ border: '1px solid', borderColor: 'secondary.main' }}>
                        <FilterList />
                    </IconButton>
                </Box>
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
                    <IconButton color="secondary" onClick={() => setMobileActionsOpen(true)} size="large" aria-label="Toolbar actions" sx={{ border: '1px solid', borderColor: 'secondary.main' }}>
                        <MenuIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <TextField label="Search screen" variant="outlined" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flexGrow: 1, minWidth: 200 }} />
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>{filterControls(false)}</Box>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                    {viewToggle}
                    <Tooltip title="Refresh">
                        <Button variant="contained" color="secondary" onClick={() => void load()} size="small" sx={{ minWidth: 36, px: 1, flexShrink: 0 }} aria-label="Refresh">
                            <Refresh fontSize="small" />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            <Dialog open={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Search & Filters</DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField label="Search screen" size="small" value={search} onChange={(e) => setSearch(e.target.value)} fullWidth />
                        {filterControls(true)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileFiltersOpen(false)} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={mobileActionsOpen} onClose={() => setMobileActionsOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Actions</DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2">View:</Typography>
                        {viewToggle}
                        <Button variant="contained" color="secondary" size="small" startIcon={<Refresh />} onClick={() => { setMobileActionsOpen(false); void load(); }}>
                            Refresh
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileActionsOpen(false)} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && visible.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <TvIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {screens.length === 0 ? 'No screens yet' : 'No screens match your search'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {screens.length === 0 ? 'Register a screen from Manage Screens first.' : 'Try a different search.'}
                    </Typography>
                </Paper>
            )}

            {viewMode === 'tile' && visible.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={2.5} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                    {visible.map((screen) => (
                        <Box
                            key={screen._id}
                            onClick={() => openScreen(screen._id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openScreen(screen._id);
                                }
                            }}
                            sx={{
                                width: 300,
                                height: 200,
                                borderRadius: 2.5,
                                overflow: 'hidden',
                                position: 'relative',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                cursor: 'pointer',
                                flexShrink: 0,
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 2 },
                            }}
                        >
                            <Box sx={{ position: 'absolute', inset: 0, background: heroGradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <TvIcon sx={{ fontSize: 72, color: 'rgba(255,255,255,0.85)' }} />
                            </Box>
                            <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip label={screen.online ? 'Online' : 'Offline'} color={screen.online ? 'success' : 'default'} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }} />
                                <Chip label={screen.deviceStatus?.clientVersion || 'no version'} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)' }} />
                                <Chip label={screen.deviceStatus?.resolution || 'no resolution'} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)' }} />
                            </Box>
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2, px: 1.5, py: 1.25, background: 'linear-gradient(to top, rgba(0,0,0,0.88), rgba(0,0,0,0.2))' }}>
                                <Typography variant="subtitle2" noWrap sx={{ color: '#fff', fontWeight: 700 }}>{screen.name}</Typography>
                                <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}>
                                    {[screen.location || screen.deviceStatus?.ip || 'No location', formatAgo(screen.lastSeenAt, now)].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {viewMode === 'table' && visible.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Version</TableCell>
                                <TableCell>Resolution</TableCell>
                                <TableCell>Location</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visible.map((screen) => (
                                <TableRow key={screen._id} hover sx={{ cursor: 'pointer' }} onClick={() => openScreen(screen._id)}>
                                    <TableCell>{screen.name}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={screen.online ? 'Online' : 'Offline'} color={screen.online ? 'success' : 'default'} />
                                    </TableCell>
                                    <TableCell>{screen.deviceStatus?.clientVersion || '—'}</TableCell>
                                    <TableCell>{screen.deviceStatus?.resolution || '—'}</TableCell>
                                    <TableCell>{screen.location || '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};
